import { arrayOf, bool, func, object, shape, string } from 'prop-types';
import React from 'react';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { genID } from 'ravenjs/utils/generate';
import Menu from 'ravenjs/lib/Menu';
import MenuContent from 'ravenjs/lib/MenuContent';
import MenuItem from 'ravenjs/lib/MenuItem';
import { getThemeProps } from 'ravenjs/utils/theme';
import RavenArrowDownIcon from 'ravenjs/lib/SvgIcon/types/ArrowDownIcon';

import { ArrowDownIcon as ArrowDownIconV2, ArrowUpIcon as ArrowUpIconV2 } from 'components/Icons';
import SubNavItem from 'components/SubNavItem';
import {
    DESKTOP_BREAKPOINT,
    DROPDOWN_OPEN_EVENT,
    HOVER_INTENT_DELAY,
    IMPERSONATION_RESTRICTED_SUB_NAV_ITEMS,
    SUBMENU_CLOSE_DELAY,
} from 'constants/navigation';
import MESSAGES from 'constants/messages';
import { EVENTS } from 'constants/events';
import { MNL_INTELLIGENCE } from 'constants/intelligence';
import Acl from 'modules/acl';
import { selectors as authSelectors } from 'modules/auth';
import { selectors as userSelectors } from 'modules/user';
import { actions as pendoActions } from 'modules/pendo';
import { isClientSolutionReportDisabled } from 'utils/reporting';
import { isNavigationV2Enabled as isNavV2Enabled } from 'utils/company';

import { MIGRATED_COLORS } from 'constants/quartz/migrated_colors';
import SubNavItemsStyled from './SubNavItemsStyled';

const SubNavDropdownContentV1 = styled(MenuContent)`
    background-color: ${MIGRATED_COLORS.white};
    border: 1px solid ${MIGRATED_COLORS.black};
    border-radius: 0;
    box-shadow: none;
    padding: 12px 0 12px 8px;
    width: 356px;
    box-sizing: border-box;
    overflow-y: auto;
    overflow-x: hidden;
`;

const SubNavDropdownContentV2 = styled(MenuContent)`
    background-color: ${MIGRATED_COLORS.white};
    border: 1px solid ${MIGRATED_COLORS.grey22};
    border-radius: 4px;
    box-shadow: 0 2px 8px -2px ${MIGRATED_COLORS.grey61};
    padding: 12px 8px;
    margin-top: 8px;
    width: 356px;
    max-height: 400px;
    box-sizing: border-box;
    overflow-y: auto;
    overflow-x: hidden;
`;

const SubNavDropdownButton = styled.button`
    align-items: center;
    background-color: transparent;
    border: 0;
    border-bottom-color: transparent;
    border-bottom-style: solid;
    border-bottom-width: 3px;
    color: ${getThemeProps('palette.common.dark')};
    display: flex;
    font-size: 14px;
    font-family: inherit;
    height: 100%;
    justify-content: center;
    margin: 0;
    padding: 12px;
    text-decoration: none !important;
    transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out;
    white-space: nowrap;
    cursor: pointer;
    ${getThemeProps('SubNavItem.styles')};
    ${({ $active }) => ($active ? getThemeProps('SubNavItem.active') : null)};
    ${({ $isNavV2Enabled }) =>
        $isNavV2Enabled
            ? `
        &:hover {
            color: ${getThemeProps('palette.common.dark')} !important;
        }
    `
            : ''};
`;

const DropdownButtonContent = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
`;

const DropdownArrowIconWrapper = styled.span`
    display: inline-flex;
    align-items: center;
    margin-left: 1px;
`;

const DropdownItemLabel = styled.span`
    font-weight: 400;
    font-family: Roboto, serif;
    font-size: 16px;
    line-height: 150%;
    color: ${MIGRATED_COLORS.black};
    vertical-align: middle;
    display: block;
`;

const DropdownItemDescription = styled.span`
    font-family: Roboto, serif;
    font-size: 14px;
    line-height: 150%;
    color: ${MIGRATED_COLORS.grey27};
    font-weight: 400;
    vertical-align: middle;
    display: block;
    margin-top: 4px;
    overflow-wrap: break-word;
    white-space: normal;
    max-width: 100%;
    word-wrap: break-word;
`;

const SubNavDropdownLinkV1 = styled(NavLink)`
    background-color: ${MIGRATED_COLORS.white};
    clear: both;
    cursor: pointer;
    display: block;
    font-weight: normal;
    line-height: 1.5;
    padding: 12px 20px;
    text-decoration: none;
    white-space: nowrap;
    color: ${MIGRATED_COLORS.black} !important;
    &:hover,
    &:active {
        color: ${MIGRATED_COLORS.link} !important;
        text-decoration: none;
    }
`;

const SubNavDropdownLinkV2 = styled(NavLink)`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 4px;
    border-radius: 4px;
    padding: 12px 16px;
    box-sizing: border-box;
    text-decoration: none;
    color: ${MIGRATED_COLORS.black} !important;
    white-space: normal;
    &:hover {
        background: ${MIGRATED_COLORS.blueHighlight};
        color: ${MIGRATED_COLORS.black} !important;
        text-decoration: none;
    }
    &:hover ${DropdownItemDescription} {
        color: ${MIGRATED_COLORS.black};
    }
    &:focus {
        outline: 2px solid ${MIGRATED_COLORS.link};
        outline-offset: 2px;
    }
    &:focus:not(:focus-visible) {
        outline: none;
    }
    &:focus-visible {
        outline: 2px solid ${MIGRATED_COLORS.link};
        outline-offset: 2px;
    }
`;

const SubNavItems = ({
    flexWrap,
    isImpersonatingUser,
    items,
    justifyContent,
    sendPendoEvent,
    userCompany,
    userRole,
    currentUser,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [openDropdownId, setOpenDropdownId] = React.useState(null);
    const hoverIntentTimeoutRef = React.useRef(null);
    const closeDelayTimeoutRef = React.useRef(null);
    const dropdownNodesRef = React.useRef({});

    const isNavigationV2Enabled = isNavV2Enabled();
    const isDesktop =
        typeof globalThis !== 'undefined' &&
        typeof globalThis.innerWidth === 'number' &&
        globalThis.innerWidth >= DESKTOP_BREAKPOINT;

    const clearHoverIntentTimeout = React.useCallback(() => {
        if (hoverIntentTimeoutRef.current) {
            clearTimeout(hoverIntentTimeoutRef.current);
            hoverIntentTimeoutRef.current = null;
        }
    }, []);

    const clearCloseDelayTimeout = React.useCallback(() => {
        if (closeDelayTimeoutRef.current) {
            clearTimeout(closeDelayTimeoutRef.current);
            closeDelayTimeoutRef.current = null;
        }
    }, []);

    const notifyDropdownOpen = React.useCallback(() => {
        if (typeof globalThis === 'undefined') return;
        globalThis.dispatchEvent(
            new CustomEvent(DROPDOWN_OPEN_EVENT, {
                detail: { source: 'subnavitems' },
            })
        );
    }, []);

    const openDropdown = React.useCallback(
        dropdownId => {
            clearHoverIntentTimeout();
            clearCloseDelayTimeout();
            setOpenDropdownId(dropdownId);
            notifyDropdownOpen();
        },
        [clearCloseDelayTimeout, clearHoverIntentTimeout, notifyDropdownOpen]
    );

    const closeDropdown = React.useCallback(
        dropdownId => {
            clearHoverIntentTimeout();
            clearCloseDelayTimeout();
            setOpenDropdownId(current => (current === dropdownId ? null : current));
        },
        [clearCloseDelayTimeout, clearHoverIntentTimeout]
    );

    const handleDropdownMouseEnter = React.useCallback(
        dropdownId => {
            if (isNavigationV2Enabled && isDesktop) {
                clearCloseDelayTimeout();
                clearHoverIntentTimeout();
                hoverIntentTimeoutRef.current = setTimeout(() => {
                    openDropdown(dropdownId);
                }, HOVER_INTENT_DELAY);
                return;
            }
            openDropdown(dropdownId);
        },
        [
            clearCloseDelayTimeout,
            clearHoverIntentTimeout,
            isDesktop,
            isNavigationV2Enabled,
            openDropdown,
        ]
    );

    const handleDropdownMouseLeave = React.useCallback(
        dropdownId => {
            if (isNavigationV2Enabled && isDesktop) {
                clearHoverIntentTimeout();
                clearCloseDelayTimeout();
                closeDelayTimeoutRef.current = setTimeout(() => {
                    closeDropdown(dropdownId);
                }, SUBMENU_CLOSE_DELAY);
                return;
            }

            clearHoverIntentTimeout();
            clearCloseDelayTimeout();
            closeDelayTimeoutRef.current = setTimeout(() => {
                closeDropdown(dropdownId);
            }, 150);
        },
        [
            clearCloseDelayTimeout,
            clearHoverIntentTimeout,
            closeDropdown,
            isDesktop,
            isNavigationV2Enabled,
        ]
    );

    React.useEffect(() => {
        if (!openDropdownId) return undefined;

        const handleDocumentMouseDown = event => {
            const node = dropdownNodesRef.current[openDropdownId];
            if (node && event?.target && !node.contains(event.target)) {
                closeDropdown(openDropdownId);
            }
        };

        document.addEventListener('mousedown', handleDocumentMouseDown);

        return () => {
            document.removeEventListener('mousedown', handleDocumentMouseDown);
        };
    }, [closeDropdown, openDropdownId]);

    React.useEffect(() => {
        if (typeof globalThis === 'undefined') return undefined;

        const handleOtherDropdownOpen = event => {
            if (event?.detail?.source === 'subnavitems') return;
            if (openDropdownId) {
                closeDropdown(openDropdownId);
            }
        };

        globalThis.addEventListener(DROPDOWN_OPEN_EVENT, handleOtherDropdownOpen);
        return () => {
            globalThis.removeEventListener(DROPDOWN_OPEN_EVENT, handleOtherDropdownOpen);
        };
    }, [closeDropdown, openDropdownId]);

    React.useEffect(() => {
        return () => {
            clearHoverIntentTimeout();
            clearCloseDelayTimeout();
        };
    }, [clearCloseDelayTimeout, clearHoverIntentTimeout]);

    const filterUnauthorized = item => {
        if (item.permission) {
            return Acl.check(item.permission);
        }
        return true;
    };
    const filterLicense = item => {
        if (item?.hasLicenseCheck) {
            return !isClientSolutionReportDisabled(
                userCompany,
                userRole,
                currentUser.isParentPartnerAdmin
            );
        }
        return true;
    };

    const handleClickForPendo = item => {
        return () => {
            if (item.state === MNL_INTELLIGENCE.MINTEL_NAV_STATE) {
                const intelligencePendoEvent = EVENTS.INTELLIGENCE.REDIRECT_TO_DASHBOARD;
                intelligencePendoEvent.eventProperties.source = 'HR Compliance Dashboard Tab';
                intelligencePendoEvent.eventProperties.userName = currentUser.userName;
                intelligencePendoEvent.eventProperties.companyId = userCompany.companyId;
                intelligencePendoEvent.eventProperties.companyName = userCompany.companyName;
                sendPendoEvent(intelligencePendoEvent);
            } else if (item.state === MNL_INTELLIGENCE.MINTEL_INSIGHT_STATE) {
                const intelligencePendoEvent = EVENTS.INTELLIGENCE.REDIRECT_TO_INSIGHTS;
                intelligencePendoEvent.eventProperties.source = 'HR Compliance Insights Tab';
                intelligencePendoEvent.eventProperties.userName = currentUser.userName;
                intelligencePendoEvent.eventProperties.companyId = userCompany.companyId;
                intelligencePendoEvent.eventProperties.companyName = userCompany.companyName;
                sendPendoEvent(intelligencePendoEvent);
            }
        };
    };

    return (
        <SubNavItemsStyled flexWrap={flexWrap} justifyContent={justifyContent}>
            {items
                .filter(filterUnauthorized)
                .filter(filterLicense)
                .map(item => {
                    if (item.dropdownItems && item.dropdownItems.length > 0) {
                        const dropdownId = item.subnavItemId || item.title || genID();
                        const isDropdownActive = item.dropdownItems.some(
                            dropdownItem => location?.pathname === dropdownItem.to
                        );
                        const defaultDropdownTo = item.dropdownItems?.[0]?.to;

                        const DropdownContent = isNavigationV2Enabled
                            ? SubNavDropdownContentV2
                            : SubNavDropdownContentV1;
                        const DropdownLink = isNavigationV2Enabled
                            ? SubNavDropdownLinkV2
                            : SubNavDropdownLinkV1;

                        return (
                            <li
                                key={dropdownId}
                                style={{ display: 'flex' }}
                                ref={node => {
                                    dropdownNodesRef.current[dropdownId] = node;
                                }}
                            >
                                <Menu
                                    open={openDropdownId === dropdownId}
                                    onOpen={() => openDropdown(dropdownId)}
                                    onClose={() => closeDropdown(dropdownId)}
                                    placement="bottom-start"
                                    PopperProps={{ hasFlip: false }}
                                    ContentComponent={DropdownContent}
                                    IconComponent={null}
                                    ButtonComponent={
                                        <SubNavDropdownButton
                                            $active={isDropdownActive}
                                            $isNavV2Enabled={isNavigationV2Enabled}
                                        />
                                    }
                                    ButtonProps={{
                                        type: 'button',
                                        onMouseEnter: () => handleDropdownMouseEnter(dropdownId),
                                        onMouseLeave: () => handleDropdownMouseLeave(dropdownId),
                                        onMouseDownCapture: e => e.stopPropagation(),
                                        onClickCapture: e => e.stopPropagation(),
                                        onClick: () => {
                                            if (defaultDropdownTo) {
                                                closeDropdown(dropdownId);
                                                navigate(defaultDropdownTo);
                                                return;
                                            }

                                            if (openDropdownId === dropdownId) {
                                                closeDropdown(dropdownId);
                                            } else {
                                                openDropdown(dropdownId);
                                            }
                                        },
                                        children: (
                                            <DropdownButtonContent>
                                                {item.title}
                                                {isNavigationV2Enabled ? (
                                                    (() => {
                                                        const isOpen =
                                                            openDropdownId === dropdownId;
                                                        const handleMouseDown = e => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                        };
                                                        const handleClick = e => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            if (isOpen) {
                                                                closeDropdown(dropdownId);
                                                            } else {
                                                                openDropdown(dropdownId);
                                                            }
                                                        };
                                                        const ArrowIcon = isOpen
                                                            ? ArrowUpIconV2
                                                            : ArrowDownIconV2;
                                                        return (
                                                            <DropdownArrowIconWrapper
                                                                onMouseDown={handleMouseDown}
                                                                onClick={handleClick}
                                                            >
                                                                <ArrowIcon
                                                                    width="12"
                                                                    height="12"
                                                                    fill="currentColor"
                                                                    viewBox="0 0 16 16"
                                                                />
                                                            </DropdownArrowIconWrapper>
                                                        );
                                                    })()
                                                ) : (
                                                    <RavenArrowDownIcon
                                                        height="8"
                                                        width="12"
                                                        stroke={MIGRATED_COLORS.placeholder}
                                                    />
                                                )}
                                            </DropdownButtonContent>
                                        ),
                                    }}
                                    ContentProps={{
                                        onMouseEnter: () => {
                                            clearHoverIntentTimeout();
                                            clearCloseDelayTimeout();
                                        },
                                        onMouseLeave: () => handleDropdownMouseLeave(dropdownId),
                                    }}
                                >
                                    {item.dropdownItems.map(dropdownItem => (
                                        <MenuItem key={dropdownItem.to}>
                                            <DropdownLink
                                                to={dropdownItem.to}
                                                style={{ whiteSpace: 'normal' }}
                                                onClick={() => {
                                                    closeDropdown(dropdownId);
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                    }}
                                                >
                                                    <DropdownItemLabel>
                                                        {dropdownItem.title}
                                                    </DropdownItemLabel>
                                                    {dropdownItem.description && (
                                                        <DropdownItemDescription>
                                                            {dropdownItem.description}
                                                        </DropdownItemDescription>
                                                    )}
                                                </div>
                                            </DropdownLink>
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </li>
                        );
                    }

                    const disabled =
                        isImpersonatingUser &&
                        IMPERSONATION_RESTRICTED_SUB_NAV_ITEMS.includes(item.to);
                    const subNavItemProps = disabled
                        ? {
                              ...item,
                              disabled: true,
                              onClick: e => {
                                  e.preventDefault();
                              },
                              title: MESSAGES.RESTRICTED_NAV_ITEM,
                          }
                        : { ...item };

                    return (
                        <SubNavItem
                            onClick={handleClickForPendo(item)}
                            key={item.subnavItemId || genID()}
                            {...subNavItemProps}
                        >
                            {item.title}
                        </SubNavItem>
                    );
                })}
        </SubNavItemsStyled>
    );
};

SubNavItems.propTypes = {
    flexWrap: string,
    isImpersonatingUser: bool,
    sendPendoEvent: func.isRequired,
    // List of navigational items
    items: arrayOf(
        shape({
            // The title to display for the NavLink
            title: string,
            // The URL for the NavLink
            to: string,
            dropdownItems: arrayOf(
                shape({
                    title: string,
                    description: string,
                    to: string,
                })
            ),
            subnavItemId: string,
        })
    ).isRequired,
    justifyContent: string,
    currentUser: object.isRequired,
    userCompany: object.isRequired,
    userRole: string.isRequired,
};

SubNavItems.defaultProps = {
    flexWrap: 'nowrap',
    isImpersonatingUser: false,
    justifyContent: 'flex-start',
};

const mapDispatchToProps = {
    sendPendoEvent: pendoActions.sendPendoEvent,
};

const mapStateToProps = createStructuredSelector({
    isImpersonatingUser: authSelectors.isImpersonatingUser,
    userRole: userSelectors.getUserRole,
    currentUser: userSelectors.getUserInfo,
    userCompany: userSelectors.getUserCompany,
});

export { SubNavItems as SubNavItemsUnconnected };
export default connect(mapStateToProps, mapDispatchToProps)(SubNavItems);
